import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { invalidateSiteCopyCache } from "@/lib/copy";

const DEFAULT_HOME_SERVICES = [
  { key: "pool", title: "Pool Installation", text: "Custom in-ground & above-ground builds engineered for your yard, your light, your life." },
  { key: "repair", title: "Repair & Renovation", text: "Heaters, pumps, liners, plaster — restored to silence and shimmer." },
  { key: "clean", title: "Cleaning & Service", text: "Weekly visits that keep your water glass-clear and chemistry on point." },
  { key: "openings", title: "Openings & Closings", text: "Seasonal openings, winterizations, and safety covers — done right." },
];

// Logical grouping + human label for every site_copy key the backend ships.
// Order here is the order shown to the owner. `multi` flags switch to a Textarea.
const COPY_GROUPS = [
  {
    section: "Home — Hero",
    fields: [
      { k: "home.hero_eyebrow", label: "Eyebrow" },
      { k: "home.hero_title_top", label: "Headline (top line)" },
      { k: "home.hero_title_accent", label: "Headline (accent line)" },
      { k: "home.hero_body", label: "Body paragraph", multi: true },
      { k: "home.hero_cta_primary", label: "Primary button" },
      { k: "home.hero_cta_secondary", label: "Secondary button" },
      { k: "home.stat1_value", label: "Stat 1 — value" },
      { k: "home.stat1_label", label: "Stat 1 — label" },
      { k: "home.stat2_value", label: "Stat 2 — value" },
      { k: "home.stat2_label", label: "Stat 2 — label" },
      { k: "home.stat3_value", label: "Stat 3 — value" },
      { k: "home.stat3_label", label: "Stat 3 — label" },
      { k: "home.badge_title", label: "Floating badge — title" },
      { k: "home.badge_subtitle", label: "Floating badge — subtitle" },
    ],
  },
  {
    section: "Home — Quote CTA panel",
    fields: [
      { k: "home.cta_title", label: "Title" },
      { k: "home.cta_body", label: "Body", multi: true },
      { k: "home.cta_button", label: "Button" },
      { k: "home.testimonial_quote", label: "Testimonial quote", multi: true },
      { k: "home.testimonial_author", label: "Testimonial author" },
    ],
  },
  {
    section: "Home — Shop teaser",
    fields: [
      { k: "home.shop_eyebrow", label: "Eyebrow" },
      { k: "home.shop_title", label: "Title" },
      { k: "home.shop_body", label: "Body", multi: true },
      { k: "home.shop_cta", label: "Button" },
    ],
  },
  {
    section: "Services page",
    fields: [
      { k: "services.eyebrow", label: "Eyebrow" },
      { k: "services.title", label: "Title" },
      { k: "services.subtitle", label: "Subtitle", multi: true },
      ...[1, 2, 3, 4, 5, 6].flatMap((n) => [
        { k: `services.card${n}_title`, label: `Card ${n} — title` },
        { k: `services.card${n}_desc`, label: `Card ${n} — description`, multi: true },
      ]),
      { k: "services.cta_title", label: "CTA banner — title" },
      { k: "services.cta_body", label: "CTA banner — body", multi: true },
      { k: "services.cta_button", label: "CTA banner — button" },
    ],
  },
  {
    section: "Quote page",
    fields: [
      { k: "quote.eyebrow", label: "Eyebrow" },
      { k: "quote.title", label: "Title" },
      { k: "quote.subtitle", label: "Subtitle" },
      { k: "quote.guide_title", label: "Capture guide — title" },
      { k: "quote.guide_subtitle", label: "Capture guide — subtitle", multi: true },
      { k: "quote.guide_tip", label: "Capture guide — tip", multi: true },
    ],
  },
  {
    section: "Contact page",
    fields: [
      { k: "contact.eyebrow", label: "Eyebrow" },
      { k: "contact.title", label: "Title" },
      { k: "contact.subtitle", label: "Subtitle", multi: true },
      { k: "contact.cta_button", label: "Quote button" },
    ],
  },
  {
    section: "About page",
    fields: [
      { k: "about.eyebrow", label: "Eyebrow" },
      { k: "about.title", label: "Title" },
      { k: "about.card1_title", label: "Sidebar card 1 — title" },
      { k: "about.card1_text", label: "Sidebar card 1 — text", multi: true },
      { k: "about.card2_title", label: "Sidebar card 2 — title" },
      { k: "about.card2_text", label: "Sidebar card 2 — text", multi: true },
      { k: "about.card3_title", label: "Sidebar card 3 — title" },
      { k: "about.card3_text", label: "Sidebar card 3 — text", multi: true },
    ],
  },
  {
    section: "Footer",
    fields: [
      { k: "footer.tagline", label: "Tagline paragraph", multi: true },
    ],
  },
];

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/admin").then((r) => {
      const data = r.data || {};
      if (!Array.isArray(data.home_services) || data.home_services.length === 0) {
        data.home_services = DEFAULT_HOME_SERVICES;
      }
      if (!data.site_copy || typeof data.site_copy !== "object") {
        data.site_copy = {};
      }
      setS(data);
    });
  }, []);

  const allCopyKeys = useMemo(() => COPY_GROUPS.flatMap((g) => g.fields.map((f) => f.k)), []);

  const save = useCallback(async () => {
    if (!s) return;
    setBusy(true);
    try {
      const copyPayload = {};
      allCopyKeys.forEach((k) => {
        if (s.site_copy && Object.prototype.hasOwnProperty.call(s.site_copy, k)) {
          copyPayload[k] = s.site_copy[k] ?? "";
        }
      });
      const payload = {
        store_name: s.store_name,
        tagline: s.tagline,
        contact_email: s.contact_email,
        contact_phone: s.contact_phone,
        notification_email: s.notification_email,
        notification_phone: s.notification_phone,
        address: s.address,
        about: s.about,
        logo_url: s.logo_url,
        google_review_url: s.google_review_url,
        netvendor_code: s.netvendor_code,
        home_section_title: s.home_section_title,
        home_section_subtitle: s.home_section_subtitle,
        home_services: (s.home_services || []).map((it) => ({
          key: it.key,
          title: it.title || "",
          text: it.text || "",
        })),
        site_copy: copyPayload,
      };
      await api.patch("/settings/admin", payload);
      invalidateSiteCopyCache();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }, [s, allCopyKeys]);

  if (!s) return <div className="text-slate-500">Loading…</div>;

  const upd = (k, v) => setS({ ...s, [k]: v });
  const updService = (idx, field, value) => {
    const next = [...(s.home_services || [])];
    next[idx] = { ...next[idx], [field]: value };
    setS({ ...s, home_services: next });
  };
  const updCopy = (k, v) => setS({ ...s, site_copy: { ...(s.site_copy || {}), [k]: v } });

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">Settings</h1>
      <p className="mt-1 text-sm text-slate-500">Update business profile, contacts and notification recipients.</p>

      <div className="mt-6 dash-surface p-6 md:p-8">
        <h2 className="font-display text-lg font-bold text-slate-900">Business profile</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div><Label>Business name</Label><Input className="mt-1" value={s.store_name || ""} onChange={(e) => upd("store_name", e.target.value)} data-testid="settings-store-name" /></div>
          <div><Label>Tagline</Label><Input className="mt-1" value={s.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} data-testid="settings-tagline" /></div>
          <div><Label>Public phone</Label><Input className="mt-1" value={s.contact_phone || ""} onChange={(e) => upd("contact_phone", e.target.value)} data-testid="settings-contact-phone" /></div>
          <div><Label>Public email</Label><Input className="mt-1" type="email" value={s.contact_email || ""} onChange={(e) => upd("contact_email", e.target.value)} data-testid="settings-contact-email" /></div>
          <div><Label>Notification email</Label><Input className="mt-1" type="email" value={s.notification_email || ""} onChange={(e) => upd("notification_email", e.target.value)} data-testid="settings-notif-email" /></div>
          <div><Label>Notification SMS phone</Label><Input className="mt-1" value={s.notification_phone || ""} onChange={(e) => upd("notification_phone", e.target.value)} placeholder="+14234933600" data-testid="settings-notif-phone" /></div>
          <div className="md:col-span-2"><Label>Address</Label><Input className="mt-1" value={s.address || ""} onChange={(e) => upd("address", e.target.value)} data-testid="settings-address" /></div>
          <div className="md:col-span-2"><Label>Logo URL</Label><Input className="mt-1" value={s.logo_url || ""} onChange={(e) => upd("logo_url", e.target.value)} data-testid="settings-logo-url" /></div>
          <div className="md:col-span-2">
            <Label>Google review link</Label>
            <Input
              className="mt-1"
              value={s.google_review_url || ""}
              onChange={(e) => upd("google_review_url", e.target.value)}
              placeholder="https://g.page/r/...your-review-link"
              data-testid="settings-google-review-url"
            />
            <p className="mt-1 text-[11px] text-slate-500">Pasted into the customer email when an order is marked Completed. Find yours under Google Business Profile → Get more reviews.</p>
          </div>
          <div className="md:col-span-2">
            <Label>NetVendor vendor code</Label>
            <Input
              className="mt-1"
              value={s.netvendor_code || ""}
              onChange={(e) => upd("netvendor_code", e.target.value)}
              placeholder="e.g. FIX-IT-2026"
              data-testid="settings-netvendor-code"
            />
            <p className="mt-1 text-[11px] text-slate-500">Your Fix It Aquatics vendor code on NetVendor. When set, we show a "NetVendor verified" trust badge in the footer so property managers can confirm you're an approved contractor on their platform.</p>
          </div>
          <div className="md:col-span-2"><Label>About / story (used on the public About page)</Label><Textarea rows={8} className="mt-1" value={s.about || ""} onChange={(e) => upd("about", e.target.value)} placeholder="Leave blank to use the default story. Separate paragraphs with a blank line." data-testid="settings-about" /></div>
        </div>
      </div>

      <div className="mt-6 dash-surface p-6 md:p-8">
        <h2 className="font-display text-lg font-bold text-slate-900">Home page — service tiles</h2>
        <p className="mt-1 text-xs text-slate-500">These four cards appear in the “What we do, beautifully” section on the home page.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div><Label>Section heading</Label><Input className="mt-1" value={s.home_section_title || ""} onChange={(e) => upd("home_section_title", e.target.value)} data-testid="settings-home-section-title" /></div>
          <div><Label>Section subtitle</Label><Input className="mt-1" value={s.home_section_subtitle || ""} onChange={(e) => upd("home_section_subtitle", e.target.value)} data-testid="settings-home-section-subtitle" /></div>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {(s.home_services || []).map((it, idx) => (
            <div key={it.key || idx} className="rounded-2xl border border-cyan-100 bg-white/60 p-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-cyan-700">Tile {idx + 1}{it.key ? ` · ${it.key}` : ""}</div>
              <div className="mt-2"><Label>Title</Label><Input className="mt-1" value={it.title || ""} onChange={(e) => updService(idx, "title", e.target.value)} data-testid={`settings-home-service-title-${idx}`} /></div>
              <div className="mt-2"><Label>Text</Label><Textarea rows={3} className="mt-1" value={it.text || ""} onChange={(e) => updService(idx, "text", e.target.value)} data-testid={`settings-home-service-text-${idx}`} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 dash-surface p-6 md:p-8">
        <h2 className="font-display text-lg font-bold text-slate-900">Site copy</h2>
        <p className="mt-1 text-xs text-slate-500">
          Every headline, paragraph and button on the public site, organised by page. Leave a field blank to fall back to the built-in default.
        </p>
        <div className="mt-4 space-y-4">
          {COPY_GROUPS.map((group) => (
            <details key={group.section} className="rounded-2xl border border-cyan-100 bg-white/60 p-4 open:bg-white">
              <summary className="cursor-pointer select-none font-display text-sm font-bold text-slate-900">{group.section}</summary>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {group.fields.map((f) => {
                  const id = `copy-${f.k.replace(/[^a-z0-9]/gi, "-")}`;
                  const val = (s.site_copy && s.site_copy[f.k]) ?? "";
                  return (
                    <div key={f.k} className={f.multi ? "md:col-span-2" : ""}>
                      <Label htmlFor={id} className="text-xs">{f.label} <span className="ml-1 font-mono text-[10px] text-slate-400">{f.k}</span></Label>
                      {f.multi ? (
                        <Textarea id={id} rows={3} className="mt-1" value={val} onChange={(e) => updCopy(f.k, e.target.value)} data-testid={`settings-${id}`} />
                      ) : (
                        <Input id={id} className="mt-1" value={val} onChange={(e) => updCopy(f.k, e.target.value)} data-testid={`settings-${id}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="settings-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </Button>
      </div>
      <p className="mt-3 text-xs text-slate-500">Tip: Use the Setup Wizard to configure payment options.</p>
    </div>
  );
}
